<template>
  <div class="flex h-[90%] flex-col justify-between">
    <Stepper
      :value="String(step)"
      class="mb-4 basis-full"
      @update:value="(val: string) => navigateToStep(Number(val))"
    >
      <Message
        v-if="errorMessage"
        severity="error"
        class="mb-3 mt-1"
      >
        {{ errorMessage }}
      </Message>

      <StepItem
        value="1"
        class="mb-3"
      >
        <Step>
          <div class="flex flex-col text-left mb-2">
            <span>{{ steps[0].title }}</span>
            <small class="text-[#6c757d]">{{ steps[0].description }}</small>
          </div>
        </Step>
        <StepPanel>
          <FloatLabel variant="on">
            <InputText
              id="device-name"
              v-model="deviceName"
              class="w-full"
              @keyup.enter="navigateToStep(2)"
            />
            <label for="device-name">{{ $t('device.device_name') }}</label>
          </FloatLabel>

          <FloatLabel
            v-if="canSetInternalName && showInternalName"
            variant="on"
            class="mt-4 block"
          >
            <InputText
              id="device-internal-name"
              v-model="internalName"
              class="w-full"
              @keyup.enter="navigateToStep(2)"
            />
            <label for="device-internal-name">{{ $t('device.internal_name') }}</label>
          </FloatLabel>
          <p
            v-if="canSetInternalName && showInternalName"
            class="mt-1 text-xs text-[#6c757d]"
          >
            {{ $t('device.internal_name_helper') }}
          </p>

          <template v-if="requiresTenant">
            <FloatLabel
              variant="on"
              class="mt-4 block"
            >
              <Select
                v-model="selectedTenantId"
                :options="tenantOptions"
                option-label="name"
                option-value="id"
                input-id="device-setup-tenant-select"
                filter
                :loading="tenantsLoading"
                class="w-full"
                data-testid="device-setup-tenant-select"
              />
              <label for="device-setup-tenant-select">{{ $t('device.connect_tenant_label') }}</label>
            </FloatLabel>
            <Message
              v-if="tenantsError"
              severity="error"
              class="mt-2"
              data-testid="device-setup-tenant-error"
            >
              {{ tenantsError }}
            </Message>
            <p
              class="mt-1 text-xs text-[#6c757d]"
              data-testid="device-setup-tenant-notice"
            >
              {{ $t('device.connect_tenant_notice') }}
            </p>
          </template>

          <p class="mt-4 mb-3">
            {{ $t('device.explanation') }}
          </p>
        </StepPanel>
      </StepItem>

      <StepItem
        value="2"
        class="mb-3"
        :disabled="!isStepTwoAllowed || !!errorMessage"
      >
        <Step :disabled="!isStepTwoAllowed || !!errorMessage">
          <div class="flex flex-col text-left mb-2">
            <span>{{ steps[1].title }}</span>
            <small class="text-[#6c757d]">{{ steps[1].description }}</small>
          </div>
        </Step>
        <StepPanel>
          <FloatLabel variant="on">
            <InputGroup>
              <IconField>
                <InputIcon class="pi pi-key" />
                <InputText
                  id="verification-key"
                  v-model="verificationKey"
                  disabled
                  class="w-full"
                />
              </IconField>
              <InputGroupAddon>
                <Button
                  v-tooltip.top="$t('common.copy_key')"
                  icon="pi pi-copy"
                  severity="secondary"
                  variant="text"
                  @click="copyVerificationKey"
                />
              </InputGroupAddon>
            </InputGroup>
            <label for="verification-key">{{ $t('device.verification_short_key') }}</label>
          </FloatLabel>

          <p
            v-if="showConnectDeviceHeader"
            class="mt-4 mb-3"
          >
            {{ $t('device.explanation_key') }}
          </p>
        </StepPanel>
      </StepItem>
    </Stepper>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, PropType, onMounted, watch } from 'vue';
  import apiClient from '@/services/axios';
  import axios from 'axios';
  import { useI18n } from 'vue-i18n';
  import { useToast } from 'primevue/usetoast';
  import { Device } from '@/types/device';
  import { Tenant } from '@/types/tenant';
  import { extractCollection } from '@/composables/admin/useAdminUserManagement';
  import { resolveApiErrorFromError } from '@/utils/resolveApiError';
  import { useLoading } from '@/composables/useLoading';
  import { useAuthStore } from '@/stores/authStore';

  import Stepper from 'primevue/stepper';
  import StepItem from 'primevue/stepitem';
  import Step from 'primevue/step';
  import StepPanel from 'primevue/steppanel';
  import Button from 'primevue/button';
  import InputText from 'primevue/inputtext';
  import IconField from 'primevue/iconfield';
  import InputIcon from 'primevue/inputicon';
  import InputGroup from 'primevue/inputgroup';
  import InputGroupAddon from 'primevue/inputgroupaddon';
  import FloatLabel from 'primevue/floatlabel';
  import Select from 'primevue/select';
  import Message from 'primevue/message';
  import Tooltip from 'primevue/tooltip';

  export default defineComponent({
    name: 'DeviceSetupComponent',
    components: {
      Stepper,
      StepItem,
      Step,
      StepPanel,
      Button,
      InputText,
      IconField,
      InputIcon,
      InputGroup,
      InputGroupAddon,
      FloatLabel,
      Message,
      Select,
    },
    directives: {
      tooltip: Tooltip,
    },
    props: {
      pendingDevice: {
        type: Object as PropType<Device | null>,
        default: null,
      },
      showInternalName: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['update:deviceName', 'device-added', 'close'],
    setup(props, { emit }) {
      const deviceName = ref('');
      const internalName = ref('');
      const verificationKey = ref('');
      const step = ref(1);
      const selectedType = ref('enbas');
      const isLoading = ref(false);
      const isStepTwoAllowed = ref(false);
      const errorMessage = ref('');
      const { t } = useI18n();
      const toast = useToast();
      const authStore = useAuthStore();
      const { showLoading, hideLoading } = useLoading();
      const showAddNewDevice = ref(false);
      const showConnectDeviceHeader = ref(false);

      const canSetInternalName = computed(
        () =>
          authStore.isTechnician ||
          authStore.isOrgAdmin ||
          authStore.isTenantOwner ||
          authStore.isTenantAdmin
      );

      const TENANTS_PAGE_SIZE = 100;
      const requiresTenant = computed(() => authStore.isOrgAdmin);
      const tenants = ref<Tenant[]>([]);
      const selectedTenantId = ref('');
      const tenantsLoading = ref(false);
      const tenantsError = ref('');

      // TC16: a tenant awaiting its owner cannot receive devices.
      const tenantOptions = computed(() =>
        tenants.value
          .filter((tenant) => tenant.awaitingOwner !== true)
          .map((tenant) => ({ id: tenant.id, name: tenant.name }))
      );

      const canStartConnection = computed(
        () =>
          !!deviceName.value &&
          !errorMessage.value &&
          (!requiresTenant.value || !!selectedTenantId.value)
      );

      const loadTenants = async () => {
        tenantsLoading.value = true;
        tenantsError.value = '';
        const loaded: Tenant[] = [];
        try {
          for (let page = 1; ; page++) {
            const response = await apiClient.get('/tenants', {
              headers: { Accept: 'application/ld+json' },
              params: { page: String(page), itemsPerPage: String(TENANTS_PAGE_SIZE) },
            });
            const members = extractCollection<Tenant>(response.data);
            const total = Number(response.data?.['hydra:totalItems'] ?? 0);
            loaded.push(...members);
            if (members.length === 0 || loaded.length >= total) break;
          }
          tenants.value = loaded;
        } catch (error) {
          tenants.value = [];
          tenantsError.value = resolveApiErrorFromError(error, t);
        } finally {
          tenantsLoading.value = false;
        }
      };

      watch(selectedTenantId, () => {
        if (!isStepTwoAllowed.value) {
          errorMessage.value = '';
        }
      });

      const steps = [
        { title: t('steps.new_device_title'), description: t('steps.new_device_description') },
        { title: t('steps.enter_key_title'), description: t('steps.enter_key_description') },
      
      ];

      

      const copyVerificationKey = async () => {
        if (verificationKey.value) {
          try {
            await navigator.clipboard.writeText(verificationKey.value);
          } catch (err) {
            console.error("Failed to copy text: ", err);
          } finally {
            toast.add({ severity: 'success', summary: t('common.copied'), life: 3000 });
          }
        }
      };

      const fetchSetupKeyForPendingDevice = async (deviceId: string) => {
        try {
          step.value = 2; 
          showLoading(); 
          showConnectDeviceHeader.value = true;
          const response = await apiClient.get(`/devices/${deviceId}`);
          const setupKeyShortCode = response.data.setupKeyShortCode;

          if (setupKeyShortCode) {
            verificationKey.value = setupKeyShortCode;
            isStepTwoAllowed.value = true;
          } else {
            errorMessage.value = t('errors.no_setup_key');
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            errorMessage.value = error.response?.data['hydra:description'] || error.message;
          } else {
            console.error('Unexpected error:', error);
            errorMessage.value = t('errors.unknown') + error;
          }
        } finally {
          hideLoading();
        }
      };

      const addDeviceFunction = async () => {
        if (requiresTenant.value && !selectedTenantId.value) {
          return;
        }
        const deviceData: { name: string; type: string; technicianName?: string; tenantId?: string } = {
          name: deviceName.value,
          type: selectedType.value,
        };
        if (requiresTenant.value) {
          deviceData.tenantId = selectedTenantId.value;
        }
        if (canSetInternalName.value && props.showInternalName && internalName.value.trim()) {
          deviceData.technicianName = internalName.value.trim();
        }
        isLoading.value = true;
        errorMessage.value = '';
        try {
          const response = await apiClient.post('/devices', deviceData);
          const setupKey = response.data.setupKeyShortCode;
          if (setupKey) {          
            // const setupKeyResponse = await apiClient.get(`/setup_keys/${setupKeyId}`);
            verificationKey.value = setupKey;
            isStepTwoAllowed.value = true;
            step.value = 2;
            emit('device-added', response.data);
          } else {
            errorMessage.value = t('errors.no_setup_key');
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            errorMessage.value = error.response?.data['hydra:description'] || error.message;
          } else {
            console.error('Unexpected error:', error);
            errorMessage.value = t('errors.unknown') + error;
          }
        } finally {
          isLoading.value = false;
        }
      };

      const navigateToStep = async (targetStep: number) => {
        if (errorMessage.value && targetStep > 1) {
          return;
        }

        if (targetStep === 2) {
          if (canStartConnection.value && !isStepTwoAllowed.value) {
            await addDeviceFunction();
          }
          if (!isStepTwoAllowed.value) return;
        }

        if (targetStep === 3 && !isStepTwoAllowed.value) {
          return;
        }

        if (targetStep === 1) {
          isStepTwoAllowed.value = false;
        }

        step.value = targetStep;
      };

      const addAnotherDevice = () => {
        step.value = 1;
        deviceName.value = '';
        internalName.value = '';
        verificationKey.value = '';
        selectedTenantId.value = '';
        isStepTwoAllowed.value = false;
        errorMessage.value = '';
      };

      onMounted(() => {
        // If there's a pending device, fetch its setup key and go to step 2
        if (props.pendingDevice) {
          showAddNewDevice.value = false;
          deviceName.value = props.pendingDevice.name;
          selectedType.value = props.pendingDevice.type;
          fetchSetupKeyForPendingDevice(props.pendingDevice.id);
        }else{
          showAddNewDevice.value = true;
        }
        if (requiresTenant.value) {
          loadTenants();
        }
      });

      return {
        deviceName,
        internalName,
        canSetInternalName,
        verificationKey,
        step,
        selectedType,
        addDeviceFunction,
        isLoading,
        isStepTwoAllowed,
        steps,
        navigateToStep,
        errorMessage,
        addAnotherDevice,
        copyVerificationKey,
        showAddNewDevice,
        showConnectDeviceHeader,
        requiresTenant,
        tenantOptions,
        selectedTenantId,
        tenantsLoading,
        tenantsError,
        canStartConnection,
      };
    },
  });
  </script>

  <style scoped>
  .header-title {
    font-weight: 600;
  }

  .header-subtitle {
    color: #6c757d;
  }

 
  :deep(.p-step-active .p-step-number) {
    background: #7190a6;
    color: #fff;
    border-color: #7190a6;
  }

  :deep(.p-stepitem) .p-stepper-separator,
  :deep(.p-stepitem) [data-pc-section="separator"] {
    display: block !important;
    visibility: visible !important;
    background: #c6d3db;
    min-height: 2rem;
    width: 2px;
  }

  :deep(.p-stepitem:last-child) .p-stepper-separator,
  :deep(.p-stepitem:last-child) [data-pc-section="separator"] {
    display: none !important;
  }

  </style>